"use client";

import { useCallback, useSyncExternalStore } from "react";

/**
 * Streamer "hide balance" switch. Device-local (localStorage), client-only:
 * every surface that prints the user's wallet balance reads this and shows
 * BALANCE_MASK instead. Card values, pack prices and sellback amounts are not
 * the balance and stay visible.
 */
export const BALANCE_HIDDEN_STORAGE_ITEM = "mot-balance-hidden-v1";
export const BALANCE_MASK = "••••";

const listeners = new Set<() => void>();
// Fallback when storage is blocked (private mode): hiding still works for this page load.
let memoryHidden = false;
let storageFailed = false;

function readHidden(): boolean {
  if (storageFailed) return memoryHidden;
  try {
    return window.localStorage.getItem(BALANCE_HIDDEN_STORAGE_ITEM) === "1";
  } catch {
    return memoryHidden;
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  // Another tab flipping the switch flips it here too.
  const onStorage = (event: StorageEvent) => {
    if (event.key === BALANCE_HIDDEN_STORAGE_ITEM) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

// The server cannot see localStorage, so the server render (and hydration)
// is masked; a stored "hidden" can never flash the real number on reload.
const getServerSnapshot = () => true;

export function setBalanceHidden(hidden: boolean) {
  memoryHidden = hidden;
  try {
    if (hidden) window.localStorage.setItem(BALANCE_HIDDEN_STORAGE_ITEM, "1");
    else window.localStorage.removeItem(BALANCE_HIDDEN_STORAGE_ITEM);
  } catch {
    // Storage blocked (private mode): readHidden falls back to memoryHidden.
    storageFailed = true;
  }
  for (const listener of listeners) listener();
}

export function useBalanceHidden() {
  const hidden = useSyncExternalStore(subscribe, readHidden, getServerSnapshot);
  const toggle = useCallback(() => setBalanceHidden(!readHidden()), []);
  return { hidden, toggle };
}

/** `formatted` unless the balance is hidden. */
export function maskBalance(formatted: string, hidden: boolean): string {
  return hidden ? BALANCE_MASK : formatted;
}
